import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, Users } from "lucide-react";

import { adminUsersApi } from "@/api/admin";
import { Alert } from "@/components/Alert";
import { DataTable } from "@/components/DataTable";
import { EmptyState } from "@/components/EmptyState";
import { ListToolbar } from "@/components/ListToolbar";
import { PageBody, PageHeader, PageShell } from "@/components/PageShell";
import { PageSkeleton } from "@/components/PageSkeleton";
import { Pagination } from "@/components/Pagination";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useResourceList } from "@/hooks/useResourceList";

const ROLE_LABELS = {
  owner: "Owner",
  admin: "Administrator",
  coordinator: "Care coordinator",
  caregiver: "Caregiver",
};

const COLUMNS = [
  {
    key: "full_name",
    header: "Name",
    render: (row) => <span className="font-medium">{row.full_name || "—"}</span>,
  },
  { key: "email", header: "Email", render: (row) => row.email },
  {
    key: "role",
    header: "Role",
    render: (row) => ROLE_LABELS[row.role] ?? row.role,
  },
  {
    key: "agency",
    header: "Organization",
    render: (row) => row.agency_details?.name ?? (row.is_superuser ? "Platform Admin" : "—"),
  },
  {
    key: "is_superuser",
    header: "Superuser",
    render: (row) => (row.is_superuser ? "Yes" : "No"),
  },
  {
    key: "is_active",
    header: "Status",
    render: (row) => (
      <Badge variant={row.is_active ? "default" : "secondary"}>
        {row.is_active ? "Active" : "Inactive"}
      </Badge>
    ),
  },
];

export function UsersPage() {
  const navigate = useNavigate();
  const { rows, count, isLoading, error, page, setPage, search, setSearch } =
    useResourceList(adminUsersApi);

  if (isLoading && rows.length === 0) {
    return <PageSkeleton />;
  }

  return (
    <PageShell>
      <PageHeader
        title="Users"
        description="Manage all user accounts across all tenant organizations."
        actions={
          <Button asChild>
            <Link to="/admin/users/new">
              <Plus />
              Add user
            </Link>
          </Button>
        }
      />

      <PageBody>
        <ListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search users"
        />

        <Alert>{error}</Alert>

        {rows.length === 0 ? (
          <EmptyState
            icon={search ? Search : Users}
            tone={search ? "slate" : "indigo"}
            title={search ? "No matches for that search." : "No users yet"}
            message={
              search
                ? "Try a different name or email."
                : "Add your first user to get started."
            }
            action={
              search ? null : (
                <Button asChild>
                  <Link to="/admin/users/new">
                    <Plus />
                    Add user
                  </Link>
                </Button>
              )
            }
          />
        ) : (
          <div aria-busy={isLoading}>
            <DataTable
              columns={COLUMNS}
              rows={rows}
              getRowKey={(row) => row.id}
              onRowClick={(row) => navigate(`/admin/users/${row.id}`)}
            />
          </div>
        )}

        <Pagination page={page} count={count} onChange={setPage} />
      </PageBody>
    </PageShell>
  );
}
